import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
  Info,
  Search,
  Heart,
  MapPin,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CategoryKey, CategoryScore, ScoreBreakdown, SEOAnalysis, SEOAudit, SEOIssue, SEOSection, SEOSectionKey } from '../../types'

import { categoryAccent } from '../../lib/utils'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import LogoIcon from '../../assets/landing/logo-icon.svg'
import {
  ReportSectionCard,
  ReportSectionHeader,
  ReportSectionBody,
  ScoreChip,
} from './ReportSectionCard'

interface CategorySectionProps {
  number: number
  category: CategoryScore
  seoAnalysis?: SEOAnalysis | null
  /**
   * AI-generated SEO narrative (summary + issues), used ONLY for the
   * human-readable "AI SEO Insights" card — never for scoring. The
   * deterministic seoAnalysis above remains the sole scoring authority; the
   * AI audit's own `score` field is intentionally never read or displayed
   * (see SEOAIInsights below).
   */
  seoAudit?: SEOAudit | null
  beforeBreakdowns?: React.ReactNode
  excludeSubCategories?: string[]
}


const CATEGORY_ICONS: Record<CategoryKey, typeof Search> = {
  search_results: Search,
  guest_experience: Heart,
  local_listings: MapPin,
}

const CALLOUT_KEY: Record<CategoryKey, string> = {
  search_results: 'search',
  guest_experience: 'experience',
  local_listings: 'listings',
}

// Local Listings profile-content signals owned by the LocalListingsProfileCard.
// These are hidden from the generic Profile breakdown group (the card renders
// them with their points instead) but still count toward the issue badge.
const LOCAL_LISTINGS_CARD_SIGNALS = [
  'has_website_listing', 'missing_website_listing',
  'no_description', 'short_description', 'detailed_description',
  'has_hours', 'incomplete_hours', 'missing_hours',
  'has_phone', 'missing_phone',
  'is_verified', 'not_verified',
  'has_price_range', 'missing_price_range',
  'has_social_links', 'missing_social_links',
  'has_keywords_in_description', 'missing_keywords_in_description',
]

// ─── Cartley callout ──────────────────────────────────────────────────────────

function CartleyCallout({
  categoryKey,
  issueCount,
}: {
  categoryKey: CategoryKey
  issueCount: number
}) {
  const { t } = useTranslation('report')
  if (issueCount === 0) return null

  const slug = CALLOUT_KEY[categoryKey]
  const msgKey = `category.callout.${slug}.message`
  const ctaKey = `category.callout.${slug}.cta`
  const message = t(msgKey, { appName: APP_NAME })
  const cta = t(ctaKey)

  if (message === msgKey) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="flex items-start gap-3 rounded-[14px] border px-4 py-4"
      style={{ borderColor: `${BRAND.red}30`, backgroundColor: `${BRAND.red}08` }}
    >
      <img src={LogoIcon} alt="logo" className="h-7 w-7 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-xs font-bold text-[#01114f]">
          {t('sectionCallout.title', { appName: APP_NAME })}
        </p>
        <p className="mb-2 text-xs leading-relaxed text-[#4a5565]">{message}</p>
        <a
          href={CONSULTATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold text-[#ef042d] transition-opacity hover:opacity-90"
        >
          {cta}
        </a>
      </div>
    </motion.div>
  )
}

// ─── Breakdown row ────────────────────────────────────────────────────────────

function BreakdownItem({
  bd,
  overrideLabel,
  details,
}: {
  bd: ScoreBreakdown
  overrideLabel?: string
  details?: React.ReactNode
}) {
  const { t } = useTranslation('report')
  const [open, setOpen] = useState(false)

  const Icon = bd.is_positive
    ? CheckCircle2
    : bd.severity === 'critical'
      ? XCircle
      : AlertCircle

  const iconColor = bd.is_positive
    ? 'text-[#00a63e]'
    : bd.severity === 'critical'
      ? 'text-[#ef042d]'
      : 'text-[#d08700]'

  const accentColor = bd.is_positive
    ? '#00a63e'
    : bd.severity === 'critical'
      ? '#ef042d'
      : '#d08700'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="group flex w-full items-center gap-3 px-4 py-3.5 text-start transition-colors hover:bg-white/70"
      >
        <span
          className="absolute inset-y-2 inset-s-0 w-[3px] rounded-full opacity-80"
          style={{ backgroundColor: accentColor }}
          aria-hidden
        />
        <Icon className={`h-4 w-4 shrink-0 ${iconColor}`} strokeWidth={2} />
        <p className="min-w-0 flex-1 text-sm leading-snug text-[#1e2939]">
          {overrideLabel ?? t(`breakdown.${bd.signal}`, { ...(bd.label_params ?? {}), defaultValue: bd.label })}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-medium tabular-nums text-[#6a7282]">
            {t('category.pts', { points: bd.points, max: bd.max_points })}
          </span>
          {open ? (
            <ChevronUp className="h-3.5 w-3.5 text-[#99a1af]" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-[#99a1af] opacity-60 group-hover:opacity-100" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-3"
          >
            {details ?? (
              <p className="rounded-[10px] bg-white px-3 py-2.5 text-xs leading-relaxed text-[#6a7282]">
                {bd.evidence_ref}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function StaticBreakdownItem({ label }: { label: string }) {
  return (
    <div className="relative group flex w-full items-center gap-3 px-4 py-3.5 text-start">
      <span className="absolute inset-y-2 inset-s-0 w-[3px] rounded-full bg-[#d5d7dc] opacity-80" aria-hidden />
      <AlertCircle className="h-4 w-4 shrink-0 text-[#99a1af]" strokeWidth={2} />
      <p className="min-w-0 flex-1 text-sm leading-snug text-[#364153]">{label}</p>
      <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-medium tabular-nums text-[#99a1af]">
        --
      </span>
    </div>
  )
}

function getSEOItemStatus(
  section?: SEOSection,
  fallbackBreakdown?: ScoreBreakdown,
): 'pass' | 'warning' | 'fail' {
  if (section?.checks?.length) {
    // Neutral (unverifiable, e.g. cross-script) AND Informational
    // (advisory-only, e.g. keyword/name relevance) checks are excluded from
    // the pass/fail tally entirely — mirroring the backend's seoItemPoints —
    // so they never fabricate a "warning"/"fail" state from a comparison
    // that simply couldn't be verified, or from a recommendation that was
    // never meant to be scored. See SEOCheck.neutral / SEOCheck.informational.
    const verifiable = section.checks.filter(ch => !ch.neutral && !ch.informational)
    if (verifiable.length === 0) {
      // Every check was unverifiable/informational: fall back to the
      // section's own status rather than fabricating a pass/fail signal
      // from zero verifiable checks.
      if (section.status === 'pass') return 'pass'
      if (section.status === 'fail') return 'fail'
      return 'warning'
    }
    const passed = verifiable.filter(ch => ch.passed).length
    if (passed === verifiable.length) return 'pass'
    if (passed === 0) return 'fail'
    return 'warning'
  }
  if (!fallbackBreakdown) return 'warning'
  if (fallbackBreakdown.is_positive) return 'pass'
  return fallbackBreakdown.severity === 'warning' ? 'warning' : 'fail'
}

// ─── AI SEO Insights (narrative layer) ────────────────────────────────────────
//
// The deterministic checklist above (seoAnalysis) is the sole scoring
// authority and the source of truth for structural facts (element present,
// H1 count, length, schema, alt coverage). This card instead surfaces the
// AI-generated SEO audit's SEMANTIC narrative — e.g. whether non-Latin-script
// page content actually reads as relevant to the business name/category/city,
// which the deterministic engine can only mark "unverifiable" for. Per the
// product decision: only the AI audit's `summary` and `issues` are rendered;
// its own `score` is intentionally never read here, since the deterministic
// engine remains the sole scoring authority.
//
// To avoid duplicate/contradictory advice, this list is filtered using only
// STABLE STRUCTURED FIELDS on SEOIssue (severity, category) — never fuzzy
// prose matching:
//   - severity !== 'pass'   → "pass" entries are affirmations already implied
//     by the deterministic checklist's own pass state; showing them again
//     here would be redundant, not additive.
//   - category !== 'performance' → page-speed/performance commentary belongs
//     to the dedicated PageSpeed card (Guest Experience), not this SEO card.
function filterAIInsightIssues(issues: SEOIssue[]): SEOIssue[] {
  return issues.filter(issue => issue.severity !== 'pass' && issue.category !== 'performance')
}

function aiSeverityBadgeClass(sev: SEOIssue['severity']): string {
  switch (sev) {
    case 'critical':
      return 'bg-[#ffe2e2] text-[#c10007]'
    case 'warning':
      return 'bg-[#fef3c6] text-[#a65f00]'
    default:
      return 'bg-[#dbeafe] text-[#1447e6]' // info
  }
}

function SEOAIInsights({ seoAudit }: { seoAudit?: SEOAudit | null }) {
  const { t } = useTranslation('report')
  if (!seoAudit) return null

  const issues = filterAIInsightIssues(seoAudit.issues ?? [])
  if (!seoAudit.summary && issues.length === 0) return null

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
          {t('seo.title')}
        </h3>
      </div>
      <div className="overflow-hidden rounded-[14px] bg-[#f2f2f6] p-4">
        {seoAudit.summary && (
          <p className="text-xs leading-relaxed text-[#4a5565]">{seoAudit.summary}</p>
        )}
        {issues.length > 0 && (
          <div className={`space-y-2 ${seoAudit.summary ? 'mt-3' : ''}`}>
            {issues.map((issue, idx) => (
              <div key={`${issue.category}-${idx}`} className="rounded-[10px] bg-white px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${aiSeverityBadgeClass(issue.severity)}`}
                  >
                    {t(`seo.severity.${issue.severity}`)}
                  </span>
                  <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[10px] font-medium text-[#6a7282]">
                    {t(`seo.categories.${issue.category}`)}
                  </span>
                  <span className="text-xs font-semibold text-[#1e2939]">{issue.title}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-[#6a7282]">{issue.description}</p>
                {issue.recommendation && (
                  <div className="mt-1.5 flex items-start gap-1.5 text-xs text-[#6a7282]">
                    <span className="font-bold text-[#ef042d]">→</span>
                    <span>{issue.recommendation}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


function SEOSectionItem({
  label,
  section,
  fallbackBreakdown,
  details,
}: {
  label: string
  section?: SEOSection
  fallbackBreakdown?: ScoreBreakdown
  details?: React.ReactNode
}) {
  const { t } = useTranslation('report')
  const [open, setOpen] = useState(false)

  const status = getSEOItemStatus(section, fallbackBreakdown)
  const Icon =
    status === 'pass'
      ? CheckCircle2
      : status === 'warning'
        ? AlertCircle
        : XCircle

  const iconColor =
    status === 'pass'
      ? 'text-[#00a63e]'
      : status === 'warning'
        ? 'text-[#d08700]'
        : 'text-[#ef042d]'

  const accentColor =
    status === 'pass'
      ? '#00a63e'
      : status === 'warning'
        ? '#d08700'
        : '#ef042d'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="group flex w-full items-center gap-3 px-4 py-3.5 text-start transition-colors hover:bg-white/70"
      >
        <span
          className="absolute inset-y-2 inset-s-0 w-[3px] rounded-full opacity-80"
          style={{ backgroundColor: accentColor }}
          aria-hidden
        />
        <Icon className={`h-4 w-4 shrink-0 ${iconColor}`} strokeWidth={2} />
        <p className="min-w-0 flex-1 text-sm leading-snug text-[#1e2939]">{label}</p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-medium tabular-nums text-[#6a7282]">
            {fallbackBreakdown
              ? t('category.pts', { points: fallbackBreakdown.points, max: fallbackBreakdown.max_points })
              : '--'}
          </span>
          {open ? (
            <ChevronUp className="h-3.5 w-3.5 text-[#99a1af]" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-[#99a1af] opacity-60 group-hover:opacity-100" />
          )}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-3"
          >
            {details ?? (
              <p className="rounded-[10px] bg-white px-3 py-2.5 text-xs leading-relaxed text-[#6a7282]">
                {fallbackBreakdown?.evidence_ref ?? 'No details available yet.'}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Sub-category group ───────────────────────────────────────────────────────

function SubCategoryGroup({
  label,
  breakdowns,
}: {
  label: string
  breakdowns: ScoreBreakdown[]
}) {
  const positiveCount = breakdowns.filter(b => b.is_positive).length

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
          {label}
        </h3>
        <span className="text-[10px] tabular-nums text-[#99a1af]">
          {positiveCount}/{breakdowns.length}
        </span>
      </div>
      <div className="overflow-hidden rounded-[14px] bg-[#f2f2f6] divide-y divide-[#e8e8ec]">
        {breakdowns.map(bd => (
          <BreakdownItem key={bd.id} bd={bd} />
        ))}
      </div>
    </div>
  )
}

// ─── Category section ─────────────────────────────────────────────────────────

export function CategorySection({
  number,
  category,
  seoAnalysis,
  seoAudit,
  beforeBreakdowns,
  excludeSubCategories = [],
}: CategorySectionProps) {

  const { t } = useTranslation('report')
  const catKey = category.category as CategoryKey
  const accent = categoryAccent(catKey)
  const Icon = CATEGORY_ICONS[catKey] ?? Search

  // Sub-categories rendered by a dedicated card (not the generic group list).
  // "performance" (Guest Experience) is shown by the PageSpeed card. Search
  // Results "reviews" are relocated to the Local Listings profile card under
  // User-submitted content (still scored in Search Results for now).
  const hiddenSubCategories = new Set([
    ...excludeSubCategories,
    ...(catKey === 'guest_experience' ? ['performance'] : []),
    ...(catKey === 'search_results' ? ['reviews'] : []),
  ])
  // Local Listings profile-content signals are rendered with their points by the
  // LocalListingsProfileCard, so they are removed from the generic Profile group
  // (which then shows only categories / attributes / cover photo / action links).
  const hiddenSignals = new Set(
    catKey === 'local_listings' ? LOCAL_LISTINGS_CARD_SIGNALS : [],
  )

  const grouped: Record<string, ScoreBreakdown[]> = {}
  for (const bd of category.breakdowns ?? []) {
    if (hiddenSubCategories.has(bd.sub_category)) continue
    if (hiddenSignals.has(bd.signal)) continue
    if (!grouped[bd.sub_category]) grouped[bd.sub_category] = []
    grouped[bd.sub_category].push(bd)
  }

  const subCategoryLabels: Record<string, string> = {
    website: t('category.subcategory.website'),
    seo: 'SEO',
    reviews: t('category.subcategory.reviews'),
    profile: t('category.subcategory.profile'),
    photos: t('category.subcategory.photos'),
    website_ux: t('category.subcategory.website_ux'),
    rankings: t('scoring:category.subcategory.rankings'),
    keywords: t('scoring:category.subcategory.keywords'),
    competitors: t('scoring:category.subcategory.competitors'),
  }

  // Card-covered signals (hiddenSignals) still represent real issues shown in the
  // card, so they count toward the badge — only fully hidden sub-categories don't.
  const issueCount = (category.breakdowns ?? []).filter(
    b => !hiddenSubCategories.has(b.sub_category) && !b.is_positive,
  ).length

  const descriptionMap: Record<CategoryKey, string> = {
    search_results: t('category.search.desc'),
    guest_experience: t('category.experience.desc'),
    local_listings: t('category.listings.desc'),
  }

  const groupEntries = Object.entries(grouped)
  const websiteBreakdowns = grouped.website ?? []
  const seoBreakdowns = grouped.seo ?? []

  const findWebsiteBreakdown = (signals: string[]) =>
    websiteBreakdowns.find(bd => signals.includes(bd.signal))

  const findSEOBreakdown = (signals: string[]) =>
    seoBreakdowns.find(bd => signals.includes(bd.signal))

  const websiteAvailability = findWebsiteBreakdown([
    'website_reachable',
    'website_unreachable',
    'no_website',
  ])

  const searchResultsSEOItems: Array<{ label: string; signals: string[]; sectionKey: SEOSectionKey; maxPoints: number }> = [
    { label: 'H1 heading', signals: ['has_h1', 'missing_h1'], sectionKey: 'h1', maxPoints: 1 },
    { label: 'Page title tag', signals: ['has_title', 'missing_title'], sectionKey: 'title_tag', maxPoints: 1 },
    { label: 'Meta Description', signals: ['has_meta_description', 'missing_meta_description'], sectionKey: 'meta_description', maxPoints: 1 },
    { label: 'Image ALT Tags', signals: ['has_alt_tags', 'missing_alt_tags'], sectionKey: 'alt_tags', maxPoints: 1 },
    { label: 'Open Graph (Social Sharing)', signals: ['has_open_graph', 'missing_open_graph'], sectionKey: 'open_graph', maxPoints: 1 },
    { label: 'Twitter Card', signals: ['has_twitter_card', 'missing_twitter_card'], sectionKey: 'twitter_card', maxPoints: 1 },
    { label: 'Organization Schema', signals: ['has_organization_schema', 'missing_organization_schema'], sectionKey: 'organization_schema', maxPoints: 1 },
    { label: 'LocalBusiness Schema', signals: ['has_local_schema', 'missing_local_schema'], sectionKey: 'local_business_schema', maxPoints: 1 },
    { label: 'ItemList Schema', signals: ['has_item_list_schema', 'missing_item_list_schema'], sectionKey: 'item_list_schema', maxPoints: 1 },
    { label: 'Product Schema', signals: ['has_product_schema', 'missing_product_schema'], sectionKey: 'product_schema', maxPoints: 1 },
  ]
  const remainingSearchResultGroups = groupEntries.filter(
    ([sub, breakdowns]) => sub !== 'website' && sub !== 'seo' && breakdowns.length > 0,
  )
  const websiteStatus = getSEOItemStatus(undefined, websiteAvailability)
  const seoStatusCounts = searchResultsSEOItems.reduce(
    (acc, item) => {
      const bd = findSEOBreakdown(item.signals)
      const seoSection = seoAnalysis?.[item.sectionKey] as SEOSection | undefined
      const status = getSEOItemStatus(seoSection, bd)
      acc.total += 1
      if (status === 'pass') acc.pass += 1
      else if (status === 'warning') acc.warning += 1
      else acc.fail += 1
      return acc
    },
    { pass: 0, warning: 0, fail: 0, total: 0 },
  )
  const otherStatusCounts = remainingSearchResultGroups.reduce(
    (acc, [, breakdowns]) => {
      for (const bd of breakdowns) {
        if (bd.is_positive) acc.pass += 1
        else if (bd.severity === 'warning') acc.warning += 1
        else acc.fail += 1
      }
      return acc
    },
    { pass: 0, warning: 0, fail: 0 },
  )
  const searchIssuesCount =
    (websiteStatus === 'fail' ? 1 : 0) + seoStatusCounts.fail + otherStatusCounts.fail
  const searchWarningsCount =
    (websiteStatus === 'warning' ? 1 : 0) + seoStatusCounts.warning + otherStatusCounts.warning

  const renderSEODetails = (section?: SEOSection) => {
    if (!section) return null

    // Checks are split into four visually/semantically distinct groups:
    //   - failed:         a genuine, verifiable issue — red ✗.
    //   - neutral:        unverifiable (e.g. cross-script) — muted, "not
    //                      automatically verifiable" note (SEOCheck.neutral).
    //   - informational:  advisory-only keyword/name relevance checks that
    //                      are never scored and never shown as a failure,
    //                      regardless of whether they currently pass — they
    //                      are simply "recommended to have" (SEOCheck.informational).
    //   - passed:         a genuine, verifiable pass — green ✓.
    // Informational checks are filtered out of the failed/neutral/passed
    // groups so a currently-unmet recommendation is never rendered in red.
    const failedChecks = section.checks.filter(ch => !ch.passed && !ch.neutral && !ch.informational)
    const neutralChecks = section.checks.filter(ch => ch.neutral && !ch.informational)
    const informationalChecks = section.checks.filter(ch => ch.informational)
    const passedChecks = section.checks.filter(ch => ch.passed && !ch.neutral && !ch.informational)

    return (
      <div className="rounded-[10px] bg-white px-3 py-2.5">
        <div className="space-y-1.5">
          {failedChecks.map(ch => (
            <div key={ch.key} className="flex items-center gap-1.5 text-xs leading-relaxed text-[#6a7282]">
              <XCircle className="h-3 w-3 shrink-0 text-[#ef042d]" />
              <span>{ch.label}</span>
            </div>
          ))}
          {neutralChecks.map(ch => (
            <div key={ch.key} className="flex items-center gap-1.5 text-xs italic leading-relaxed text-[#99a1af]">
              <HelpCircle className="h-3 w-3 shrink-0 text-[#99a1af]" />
              <span>{ch.label} — {t('category.seoNeutralNote')}</span>
            </div>
          ))}
          {passedChecks.map(ch => (
            <div key={ch.key} className="flex items-center gap-1.5 text-xs leading-relaxed text-[#6a7282]">
              <CheckCircle2 className="h-3 w-3 shrink-0 text-[#00a63e]" />
              <span>{ch.label}</span>
            </div>
          ))}
          {informationalChecks.map(ch => (
            <div key={ch.key} className="flex items-center gap-1.5 text-xs italic leading-relaxed text-[#1447e6]">
              <Info className="h-3 w-3 shrink-0 text-[#1447e6]" />
              <span>{ch.label} — {t('category.seoInfoNote')}</span>
            </div>
          ))}
        </div>

        {section.recommendation && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-[#f3f4f6] bg-[#fafafa] px-2 py-1.5 text-xs text-[#6a7282]">
            <span className="font-bold text-[#ef042d]">→</span>
            <span>{section.recommendation}</span>
          </div>
        )}
      </div>
    )
  }


  return (
    <ReportSectionCard
      delay={number * 0.05}
      id={category.category}
      className="scroll-mt-24"
    >
      <ReportSectionHeader
        icon={Icon}
        accentColor={accent}
        title={t(`scoring:category.${category.category}`) || category.label}
        subtitle={descriptionMap[catKey] ?? ''}
        titleId={`category-${category.category}-title`}
        titleExtra={
          catKey === 'search_results' ? (
            <div className="flex items-center gap-1.5">
              <span className="rounded-full bg-[#ffe2e2] px-2 py-0.5 text-[10px] font-semibold text-[#c10007]">
                {searchIssuesCount} issues
              </span>
              <span className="rounded-full bg-[#fef3c6] px-2 py-0.5 text-[10px] font-semibold text-[#a65f00]">
                {searchWarningsCount} warnings
              </span>
            </div>
          ) : issueCount > 0 ? (
            <span className="rounded-full bg-[#ffe2e2] px-2 py-0.5 text-[10px] font-semibold text-[#c10007]">
              {t('category.issueCount', { count: issueCount })}
            </span>
          ) : undefined
        }
        right={<ScoreChip score={category.score} max={category.max_score} />}
      />

      <ReportSectionBody>
        {beforeBreakdowns}

        {catKey === 'search_results' ? (
          <>
            <div>
              <div className="mb-2 flex items-center justify-between gap-2 px-1">
                <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">Website</h3>
                <span className="text-[10px] tabular-nums text-[#99a1af]">
                  {websiteStatus === 'pass' ? 1 : 0}/1
                </span>
              </div>
              <div className="overflow-hidden rounded-[14px] bg-[#f2f2f6] divide-y divide-[#e8e8ec]">
                {websiteAvailability ? (
                  <BreakdownItem bd={websiteAvailability} />
                ) : (
                  <StaticBreakdownItem label="Website is reachable and responds" />
                )}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2 px-1">
                <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">SEO</h3>
                <span className="text-[10px] tabular-nums text-[#99a1af]">
                  {seoStatusCounts.pass}/{seoStatusCounts.total}
                </span>
              </div>
              <div className="overflow-hidden rounded-[14px] bg-[#f2f2f6] divide-y divide-[#e8e8ec]">
                {searchResultsSEOItems.map(item => {
                  const bd = findSEOBreakdown(item.signals)
                  const seoSection = seoAnalysis?.[item.sectionKey] as SEOSection | undefined
                  return (
                    <SEOSectionItem
                      key={item.label}
                      label={item.label}
                      section={seoSection}
                      fallbackBreakdown={bd}
                      details={renderSEODetails(seoSection) ?? undefined}
                    />
                  )
                })}
              </div>
            </div>

            <SEOAIInsights seoAudit={seoAudit} />

            {remainingSearchResultGroups.map(([sub, breakdowns]) => (
              <SubCategoryGroup
                key={sub}
                label={subCategoryLabels[sub] ?? sub.replace(/_/g, ' ')}
                breakdowns={breakdowns}
              />
            ))}
          </>
        ) : (

          groupEntries.map(([sub, breakdowns]) => (
            <SubCategoryGroup
              key={sub}
              label={subCategoryLabels[sub] ?? sub.replace(/_/g, ' ')}
              breakdowns={breakdowns}
            />
          ))
        )}

        {groupEntries.length === 0 && !beforeBreakdowns && (
          <div className="rounded-[14px] bg-[#f2f2f6] px-4 py-8 text-center text-sm text-[#99a1af]">
            {t('category.noBreakdown')}
          </div>
        )}

        <CartleyCallout categoryKey={catKey} issueCount={issueCount} />
      </ReportSectionBody>
    </ReportSectionCard>
  )
}
